/*
 * The public landing page.
 *
 * Its palette is the CREC mark's own (blue, red, cream), which no theme carries
 * because no theme should: the token contract in packages/theme describes a
 * signed-in reader with a ROLE, and this page has no reader at all.
 *
 * Those values now live in @harmony/theme/public.css, shared with the ARTICLES
 * section -- the same shop window seen from a second door. Two copies would
 * agree today and drift the first time one was adjusted.
 */

@import '@harmony/theme/public.css';

* {
  box-sizing: border-box;
}

html {
  -webkit-text-size-adjust: 100%;
}

body {
  margin: 0;
  background: var(--sand);
  color: var(--ink);
  font-family: var(--body);
  line-height: 1.5;
}

/*
 * Everything is inside one column, gutter included.
 *
 * A flex column so the footer can be held at the BOTTOM of the viewport: with
 * the hero and a footer and nothing else, the page is shorter than a desktop
 * screen and the footer would otherwise sit halfway up it with a band of empty
 * sand beneath. `margin-top: auto` on the footer eats the difference, and does
 * nothing at all once the content is taller than the screen.
 */
/* FULL BLEED ON A PHONE, INSET ON EVERYTHING ELSE.
   Below 720px the panel runs to all three device edges: a narrow screen has no
   width to spend on a margin, and the page reads as the page rather than as a
   card laid on one. The gutter returns at the same breakpoint the rest of the
   layout widens at, rather than at a second one invented for it.
   The FOOTER carries its own horizontal padding (see .footer), which is what
   keeps "a ministry of the CREC" off the edge here. */
.page {
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0;
}

/* ---------------------------------------------------------------- hero -- */

.hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(160deg, #00629b 0%, #024e7c 60%, #013a5e 100%);
  /* Square to the device edges on a phone; the radius returns with the gutter
     at 720px, where there is a margin for it to sit in. The shadow stays: with
     three sides off-screen it reads as the seam between the panel and the
     footer, which is where it does the most good. */
  border-radius: 0;
  box-shadow: 0 20px 50px rgb(1 42 58 / 30%);
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
}

.brand {
  display: flex;
  align-items: center;
  /* Equal air on both sides of the mark: this matches .nav's own horizontal
     padding, so the gap to the word equals the gap to the edge. Both move
     together at the wide breakpoint below. */
  gap: 18px;
  color: #fff;
  text-decoration: none;
}

/* The mark carries its own cream plate, so it gets no second one here -- a
   white square behind a cream square is two nearly-identical off-whites with a
   seam between them. Rounded only, which is how /app rounds the same artwork. */
.brandMark {
  display: block;
  width: 38px;
  height: 38px;
  border-radius: 10px;
}

/* THE WORDMARK, from @harmony/theme/wordmark.css -- the same definition the app
   bar and the sign-in card use. This page carries its own palette and its own
   display face, deliberately; the platform's NAME is the one thing that is not
   a per-surface decision, so it comes from there. */
.brandName {
  display: inline-block;
  font-size: 21px;
  font-family: var(--wordmark-font);
  font-weight: var(--wordmark-weight);
  letter-spacing: var(--wordmark-tracking);
  text-transform: var(--wordmark-transform);
  transform: translateY(var(--wordmark-optical-shift));
}

.brandName::first-letter {
  font-size: calc(1em * var(--wordmark-initial-scale));
  line-height: 0;
  margin-right: calc(var(--wordmark-tracking) * -1 * (1 - 1 / var(--wordmark-initial-scale)));
}

.loginPill {
  flex: none;
  padding: 10px 20px;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
  background: var(--red);
  border-radius: var(--round);
}

.loginPill:hover,
.loginPill:focus-visible {
  background: #8d1527;
}

/* The foot of this padding is the band between the illustration and the seam
   where the blue panel gives way to the footer. On a phone the artwork is
   BELOW the text rather than beside it, so that band is the end of the page
   and wants twice the air a side-by-side layout needs. The wide breakpoints
   set their own. */
.heroBody {
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 8px 18px 64px;
}

.eyebrow {
  margin: 0 0 14px;
  color: var(--rose);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.verse {
  margin: 0 0 18px;
  color: #fff;
  font-family: var(--display);
  font-size: clamp(29px, 5.6vw, 44px);
  font-weight: 600;
  line-height: 1.08;
  letter-spacing: 0.04em;
}

.cite {
  display: block;
  margin-top: 16px;
  color: var(--pale);
  font-family: var(--body);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.lede {
  max-width: 430px;
  margin: 0 0 26px;
  color: var(--pale);
  font-size: 17px;
  line-height: 1.6;
}

/* A CENTERED COLUMN ON A PHONE, A ROW FURTHER UP.
   Both buttons are full-shape and neither is wide enough to fill the screen, so
   side by side they wrapped into a ragged left-aligned stack that agreed with
   nothing -- the illustration beneath them is centered. Stacking them and
   centering them puts the two ways on under the middle of the artwork. The row
   returns at 720px, where there is width for both on one line.

   ONE WIDTH FOR BOTH, and not the full width of the screen: the column is
   `fit-content`, so it is as wide as the wider button, and `stretch` hands that
   width to the narrower one. Centered as a block by the auto margins. */
.heroActions {
  display: flex;
  flex-direction: column;
  gap: 22px;
  align-items: stretch;
  width: fit-content;
  margin: 28px auto 0;
}

/* ------------------------------------------------------------- buttons -- */

/*
 * Two of them, because there are two backgrounds. `btnLight` reads on the blue
 * card and would vanish on the sand; `btnSolid` is the other way about.
 */
.btnLight,
.btnSolid {
  display: inline-block;
  padding: 15px 28px;
  font: inherit;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  text-decoration: none;
  border: 0;
  border-radius: var(--round);
  cursor: pointer;
}

/* The CREC mark's own cream, which is what the brand plate behind the rings in
   the bar is: white would be a second, colder off-white beside it. */
.btnLight {
  color: var(--blue);
  background: var(--cream);
  box-shadow: 0 10px 24px rgb(0 0 0 / 22%);
}

.btnSolid {
  color: #fff;
  background: var(--red);
}

.btnSolid:hover,
.btnSolid:focus-visible {
  background: #8d1527;
}

/* THE SECOND BUTTON: the same plate, with somewhere to go.
   `inline-flex` rather than `inline-block` so the chevron sits on the text's
   center line whatever the line box does; the right padding is trimmed because
   the chevron brings its own air. */
.btnAway {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  /* Centered explicitly: stretched to the column's width (see .heroActions) a
     flex row would leave the label and the chevron packed against the left. */
  justify-content: center;
  padding-right: 20px;
}

.btnChevron {
  flex: none;
  width: 18px;
  height: 18px;
  /* Optically level with the cap height rather than the line box. */
  margin-top: 1px;
  color: var(--red);
}

/* -------------------------------------------------------- illustration -- */

.heroArt {
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 20px;
}

/* The red halo the window sits in. Behind, and never in the way of a tap. */
.heroArt::before {
  position: absolute;
  inset: 6% 50% auto auto;
  width: 240px;
  height: 320px;
  background: radial-gradient(ellipse, rgb(166 25 46 / 30%) 0%, rgb(166 25 46 / 0%) 70%);
  transform: translateX(50%);
  content: '';
}

.window {
  position: relative;
  width: 100%;
  max-width: 232px;
  height: auto;
  filter: drop-shadow(0 14px 30px rgb(0 0 0 / 30%));
}

.glow {
  transform-box: fill-box;
  transform-origin: center;
  animation: soft-pulse 4s ease-in-out infinite;
}

.heart {
  animation: drift-up 4.2s ease-in-out infinite;
}

.heart2 {
  animation-duration: 4s;
  animation-delay: 1.7s;
}

.heart3 {
  animation-duration: 4.6s;
  animation-delay: 2.7s;
}

.bride,
.groom {
  animation: bob 3.6s ease-in-out infinite;
}

.groom {
  animation-delay: 0.5s;
}

@keyframes soft-pulse {
  0%,
  100% {
    opacity: 0.5;
    transform: scale(1);
  }

  50% {
    opacity: 0.95;
    transform: scale(1.08);
  }
}

@keyframes drift-up {
  0% {
    opacity: 0;
    transform: translateY(0) scale(0.9);
  }

  20% {
    opacity: 0.9;
  }

  100% {
    opacity: 0;
    transform: translateY(-72px) scale(1.1);
  }
}

@keyframes bob {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-6px);
  }
}

/* --------------------------------------------------------------- modal -- */

/* A gutter on a phone. A <dialog> is laid out against the viewport, and the UA
   sheet's own `max-width` leaves it a couple of pixels short of the device
   edges -- which reads as a panel that failed to fit rather than as a dialog. */
.modal {
  width: calc(100% - 40px);
  max-width: 460px;
  padding: 26px 24px;
  color: var(--ink);
  background: var(--cream);
  border: 0;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgb(1 42 58 / 35%);
}

.modal::backdrop {
  background: rgb(1 42 58 / 45%);
}

.modal p {
  margin: 0 0 14px;
  color: var(--ink-soft);
  font-size: 16px;
}

.modalTitle {
  margin: 0 0 12px;
  font-family: var(--display);
  font-size: 24px;
  font-weight: 600;
}

.modal a {
  color: var(--blue);
}

.modal form {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* -------------------------------------------------------------- footer -- */

.footer {
  margin-top: auto;
  padding: 30px 16px 40px;
  color: var(--ink-soft);
  font-size: 14px;
  text-align: center;
}

.footerLead {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
}

.footerLead a {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  color: var(--blue);
}

/* The crest, kept to the height of the line it sits beside. Its own artwork is
   taller than it is wide, so the width follows rather than being stated -- a
   square box would print it letterboxed inside one. */
.crecMark {
  flex: none;
  width: auto;
  height: 22px;
}

.footerNote {
  margin: 0 0 6px;
}

.footerNote a {
  color: var(--blue);
}

/* The Terms and the Policy, on one line under the support address. Quieter
   than the CREC line above them: they must be findable, which is a legal
   requirement, and they are not what anybody came here to read. */
.footerLegal {
  margin-top: 10px;
}

.footerName {
  margin-top: 14px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  opacity: 0.6;
}

/* --------------------------------------------------------- wider than -- */

@media (width >= 720px) {
  /* The gutter and the rounded panel, together -- see .page and .hero. */
  .page {
    padding: 28px 28px 0;
  }

  .hero {
    border-radius: 30px;
  }

  .nav {
    padding: 22px 32px;
  }

  /* Kept equal to the padding above -- see .brand. */
  .brand {
    gap: 32px;
  }

  .brandName {
    font-size: 25px;
  }

  .brandMark {
    display: block;
    width: 56px;
    height: 56px;
    border-radius: 14px;
  }

  .heroBody {
    padding: 12px 32px 44px;
  }

  /* Side by side at their own widths, left-aligned with the verse above them
     -- see .heroActions. */
  .heroActions {
    flex-flow: row wrap;
    gap: 16px;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    margin: 0;
  }
}

@media (width >= 940px) {
  .heroBody {
    grid-template-columns: 1.05fr 0.95fr;
    gap: 24px;
    padding: 16px 40px 56px;
  }

  .heroArt {
    padding-top: 0;
  }

  .window {
    max-width: 256px;
  }

  .lede {
    font-size: 18px;
  }
}

/*
 * Nothing here is load-bearing; all of it is decoration, so all of it stops.
 */
@media (prefers-reduced-motion: reduce) {
  .glow,
  .heart,
  .bride,
  .groom {
    animation: none;
  }
}
